<script lang="ts">
  // Labs — every panel, every visit, with each analyte linked to the condition that
  // tracks it. Flags come from the tracking spec where a measure exists (same rule
  // as the condition status), otherwise from a plain reference range.
  import type { Dataset, Visit } from '../types.ts';
  import { MEASURES, conditionsForPath, type MeasureSpec } from '../tracking/spec.ts';
  import { inTarget, isAttention, valueAt } from '../tracking/collate.ts';
  import { fmtDate, fmtNum } from '../format.ts';

  let { ds, onOpen }: { ds: Dataset; onOpen: (id: string) => void } = $props();

  interface Col { path: string; label: string; unit: string; d?: number; ref?: [number, number] | { max: number } | { min: number }; refLabel?: string }
  const GROUPS: { id: string; label: string; cols: Col[]; every?: boolean }[] = [
    { id: 'glycemic', label: 'Glycaemic', cols: [
      { path: 'glycemic.hba1c', label: 'HbA1c', unit: '%', d: 1 }, { path: 'glycemic.hba1cMmolMol', label: 'HbA1c', unit: 'mmol/mol', ref: { max: 52 }, refLabel: '< 53' },
      { path: 'glycemic.fastingGlucose', label: 'Fasting glucose', unit: 'mg/dL' }, { path: 'glycemic.estimatedAverageGlucose', label: 'Est. average glucose', unit: 'mg/dL' } ] },
    { id: 'lipids', label: 'Fasting lipids', cols: [
      { path: 'lipids.totalCholesterol', label: 'Total cholesterol', unit: 'mg/dL' }, { path: 'lipids.ldl', label: 'LDL', unit: 'mg/dL' }, { path: 'lipids.hdl', label: 'HDL', unit: 'mg/dL' },
      { path: 'lipids.tg', label: 'Triglycerides', unit: 'mg/dL' }, { path: 'lipids.nonHdl', label: 'Non-HDL', unit: 'mg/dL' } ] },
    { id: 'renal', label: 'Renal', cols: [
      { path: 'renal.creatinine', label: 'Creatinine', unit: 'mg/dL', d: 2 }, { path: 'renal.egfr', label: 'eGFR', unit: 'mL/min/1.73m²' }, { path: 'renal.bun', label: 'BUN', unit: 'mg/dL', ref: [7, 20], refLabel: '7–20' },
      { path: 'renal.uacr', label: 'Urine ACR', unit: 'mg/g' }, { path: 'renal.potassium', label: 'Potassium', unit: 'mmol/L', d: 1 } ] },
    { id: 'liver', label: 'Liver', cols: [
      { path: 'cmp.alt', label: 'ALT', unit: 'U/L' }, { path: 'cmp.ast', label: 'AST', unit: 'U/L' }, { path: 'cmp.alkalinePhosphatase', label: 'Alk. phosphatase', unit: 'U/L', ref: [40, 130], refLabel: '40–130' },
      { path: 'cmp.totalBilirubin', label: 'Total bilirubin', unit: 'mg/dL', d: 1, ref: [0.1, 1.2], refLabel: '0.1–1.2' } ] },
    { id: 'cmp', label: 'Chemistry (CMP)', cols: [
      { path: 'cmp.sodium', label: 'Sodium', unit: 'mmol/L', ref: [135, 145], refLabel: '135–145' }, { path: 'cmp.potassium', label: 'Potassium', unit: 'mmol/L', d: 1 },
      { path: 'cmp.chloride', label: 'Chloride', unit: 'mmol/L', ref: [98, 107], refLabel: '98–107' }, { path: 'cmp.bicarbonate', label: 'Bicarbonate', unit: 'mmol/L', ref: [22, 29], refLabel: '22–29' },
      { path: 'cmp.bun', label: 'BUN', unit: 'mg/dL', ref: [7, 20], refLabel: '7–20' }, { path: 'cmp.creatinine', label: 'Creatinine', unit: 'mg/dL', d: 2 }, { path: 'cmp.glucose', label: 'Glucose (fasting)', unit: 'mg/dL', ref: [70, 99], refLabel: '70–99' },
      { path: 'cmp.calcium', label: 'Calcium', unit: 'mg/dL', d: 1, ref: [8.6, 10.3], refLabel: '8.6–10.3' }, { path: 'cmp.totalProtein', label: 'Total protein', unit: 'g/dL', d: 1, ref: [6.4, 8.3], refLabel: '6.4–8.3' },
      { path: 'cmp.albumin', label: 'Albumin', unit: 'g/dL', d: 1, ref: [3.5, 5.0], refLabel: '3.5–5.0' } ] },
  ];
  const specByPath: Record<string, MeasureSpec> = Object.fromEntries(Object.values(MEASURES).map((m) => [m.path, m]));
  // renal.potassium and cmp.potassium are the same value; share the spec.
  specByPath['cmp.potassium'] = MEASURES.potassium; specByPath['cmp.creatinine'] = MEASURES.creatinine; specByPath['cmp.bun'] = specByPath['cmp.bun'];

  let group = $state('glycemic');
  const g = $derived(GROUPS.find((x) => x.id === group)!);
  const rows = $derived([...ds.visits].reverse().filter((v) => g.cols.some((c) => valueAt(v, c.path) != null)));

  type Flag = '' | 'high' | 'low' | 'attn';
  function flag(c: Col, v: number): Flag {
    const s = specByPath[c.path];
    if (s) {
      if (isAttention(s.attention, v)) return 'attn';
      const ok = inTarget(s.target, v);
      if (ok === false) return s.direction === 'higherBetter' ? 'low' : 'high';
      return '';
    }
    if (!c.ref) return '';
    if (Array.isArray(c.ref)) return v < c.ref[0] ? 'low' : v > c.ref[1] ? 'high' : '';
    if ('max' in c.ref) return v > c.ref.max ? 'high' : '';
    return v < c.ref.min ? 'low' : '';
  }
  const refOf = (c: Col) => specByPath[c.path]?.target?.label ?? c.refLabel ?? '';
  const latest = $derived(ds.visits[ds.visits.length - 1]);
  const latestFor = (c: Col): Visit | undefined => [...ds.visits].reverse().find((v) => valueAt(v, c.path) != null);
</script>

<div class="labs">
  <div class="groups" role="tablist">
    {#each GROUPS as gr (gr.id)}<button role="tab" aria-selected={group === gr.id} class:on={group === gr.id} onclick={() => (group = gr.id)}>{gr.label}</button>{/each}
  </div>

  <section class="latest">
    {#each g.cols as c (c.path)}
      {@const v = latestFor(c)}
      {@const val = v ? valueAt(v, c.path) : null}
      {@const links = conditionsForPath(c.path)}
      <div class="lcard">
        <span class="lname">{c.label}</span>
        <span class="lval num {val != null ? flag(c, val) : ''}">{fmtNum(val, c.d ?? 0)}<small>{c.unit}</small></span>
        <span class="lmeta t-foot">{v ? fmtDate(v.date) : ''}{#if refOf(c)} · ref {refOf(c)}{/if}</span>
        {#if links.length}<span class="links">{#each links as l (l.id)}<button class="chip" onclick={() => onOpen(l.id)} title="Tracked under {l.name}">{l.shortName}</button>{/each}</span>{:else}<span class="t-foot nolink">not linked to a tracked condition</span>{/if}
      </div>
    {/each}
  </section>

  <div class="tablewrap">
    <table>
      <thead>
        <tr>
          <th class="sticky">Visit</th>
          {#each g.cols as c (c.path)}
            <th><div class="th"><span>{c.label}</span><span class="t-foot">{c.unit}{#if refOf(c)} · {refOf(c)}{/if}</span>
              {#if conditionsForPath(c.path).length}<span class="thlinks">{#each conditionsForPath(c.path) as l (l.id)}<button class="chip sm" onclick={() => onOpen(l.id)}>{l.shortName}</button>{/each}</span>{/if}</div></th>
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each rows as v (v.index)}
          <tr>
            <td class="sticky"><span class="vd">{fmtDate(v.date)}</span><span class="t-foot">age {Math.floor(v.ageYears)} · {v.eraLabel}</span></td>
            {#each g.cols as c (c.path)}
              {@const val = valueAt(v, c.path)}
              <td class="num {val != null ? flag(c, val) : ''}">{val == null ? '·' : fmtNum(val, c.d ?? 0)}</td>
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
  <p class="t-foot legend"><span class="sw high"></span> above target / range &nbsp; <span class="sw low"></span> below &nbsp; <span class="sw attn"></span> attention threshold &nbsp; · a dot means the test was not drawn at that visit. Targets are those used by the condition tracker; reference ranges are shown where no condition tracks the analyte. Latest visit {fmtDate(latest.date)}.</p>
</div>

<style>
  .labs { display: flex; flex-direction: column; gap: var(--space-4); }
  .groups { display: flex; gap: 4px; flex-wrap: wrap; }
  .groups button { padding: var(--space-2) var(--space-4); border-radius: var(--radius-pill); border: 1px solid var(--line-strong); color: var(--text-2); font-weight: 700; font-size: var(--fs-foot); background: var(--surface); }
  .groups button.on { color: var(--accent); border-color: var(--accent); background: var(--accent-soft); }
  .latest { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: var(--space-3); }
  .lcard { display: flex; flex-direction: column; gap: 2px; background: var(--surface); border: 1px solid var(--line); border-radius: var(--radius-md); padding: var(--space-3) var(--space-4); }
  .lname { font-weight: 700; }
  .lval { font-size: 24px; font-weight: 600; line-height: 1.1; }
  .lval small { font-size: var(--fs-foot); color: var(--text-3); margin-left: 4px; font-weight: 400; }
  .links { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 4px; }
  .nolink { margin-top: 4px; }
  .chip { font-size: var(--fs-foot); font-weight: 700; color: var(--accent); background: var(--accent-soft); border-radius: var(--radius-pill); padding: 1px 8px; }
  .chip:hover { background: var(--accent); color: var(--on-accent); }
  .chip.sm { padding: 0 6px; }
  .tablewrap { border: 1px solid var(--line); border-radius: var(--radius-md); overflow: auto; background: var(--surface); max-height: 70vh; }
  table { border-collapse: separate; border-spacing: 0; min-width: 100%; }
  th, td { text-align: right; padding: var(--space-2) var(--space-3); border-bottom: 1px solid var(--line); white-space: nowrap; vertical-align: top; }
  thead th { position: sticky; top: 0; background: var(--surface-2); z-index: 1; font-size: var(--fs-foot); color: var(--text-2); }
  .th { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; }
  .thlinks { display: flex; gap: 3px; }
  .sticky { position: sticky; left: 0; background: var(--surface); text-align: left; z-index: 2; }
  thead .sticky { background: var(--surface-2); z-index: 3; }
  td.sticky { display: table-cell; }
  .vd { display: block; font-weight: 600; }
  td.sticky .t-foot { display: block; }
  tbody tr:hover td { background: var(--bg); }
  td.high, .lval.high { color: var(--warn); font-weight: 700; }
  td.low, .lval.low { color: var(--accent-2); font-weight: 700; }
  td.attn, .lval.attn { color: var(--danger); font-weight: 800; }
  .legend { line-height: 1.6; }
  .sw { display: inline-block; width: 10px; height: 10px; border-radius: 2px; vertical-align: -1px; margin-right: 2px; }
  .sw.high { background: var(--warn); } .sw.low { background: var(--accent-2); } .sw.attn { background: var(--danger); }
</style>
